import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LoginForm } from '../LoginForm';
import { PARAM_AUTH_REQ_ID, PARAM_CODE, PARAM_ERROR, PARAM_STATE } from './AuthenticationParams';

type Props = {
    children: React.ReactNode;
    dispatchInitAuthentication: () => void;

    /**
     * An optional function for canceling the authentication. This button will be shown if the authentication has failed once.
     */
    dispatchCancelAuthentication?: () => void;

    /**
     * A function that will try to fetch a new access token
     */
    dispatchGetAccessToken: () => void;

    /**
     * The current access token
     */
    accessToken?: string | undefined;

    /**
     * If the login page should try to auto login without displaing a login button first.
     */
    autoLogin: boolean;
};

/**
 * A component that will make sure that the user is authenticated before the child component
 * can be rendered.
 */
const AuthenticationWrapper = ({
    children,
    dispatchInitAuthentication,
    dispatchCancelAuthentication,
    dispatchGetAccessToken,
    accessToken,
    autoLogin = true,
}: Props) => {
    const [params, setParams] = useSearchParams();

    // If we have a valid token, remove code and state parameters from url
    useEffect(() => {
        if (accessToken && (params.get(PARAM_STATE) || params.get(PARAM_CODE) || params.get(PARAM_ERROR))) {
            params.delete(PARAM_CODE);
            params.delete(PARAM_STATE);
            params.delete(PARAM_ERROR);
            params.delete(PARAM_AUTH_REQ_ID);
            setParams(params);
        }
    }, [accessToken, params, setParams]);

    // If we got authentication parameters, try and use them to get a new access token
    useEffect(() => {
        // We have access token, no need to continue
        if (accessToken) return;

        if (params.get(PARAM_CODE) && params.get(PARAM_STATE)) {
            // We got callback parameters from the auth service, use them to fetch access token
            dispatchGetAccessToken();
        } else if (autoLogin && !params.get(PARAM_ERROR)) {
            // Not authenticated, initiate auto login
            dispatchInitAuthentication();
        }
    }, [accessToken, params]);

    return (
        <>
            {accessToken && children}
            {!accessToken && (!autoLogin || params.get(PARAM_ERROR)) && (
                <LoginForm
                    dispatchInitAuthentication={dispatchInitAuthentication}
                    dispatchCancelAuthentication={dispatchCancelAuthentication}
                    error={params.get(PARAM_ERROR)}
                />
            )}
        </>
    );
};

AuthenticationWrapper.propTypes = {
    children: PropTypes.element,
};
export default AuthenticationWrapper;
